import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { HeaderBar } from './HeaderBar';
import { useWorkout } from '../hooks/useWorkout';
import { WorkoutUtils } from '../utils/workoutUtils';
import { WeeklyProgressCharts } from './charts/WeeklyProgressCharts';
import { WorkoutSummaryCharts } from './charts/WorkoutSummaryCharts';
import { MostImprovedCharts } from './charts/MostImprovedCharts';
import { PersonalRecordsCharts } from './charts/PersonalRecordsCharts';

export const StatDetail: React.FC = () => {
  const { statType } = useParams<{ statType: string }>();
  const navigate = useNavigate();
  const { userProgress, getWorkoutStats } = useWorkout();
  const stats = getWorkoutStats();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleClose = () => {
    navigate(-1); // Go back to previous page
  };

  // Get the data for the specific stat type
  const getStatData = () => {
    const overallStats = WorkoutUtils.getOverallStats(userProgress);
    const strengthRecords = WorkoutUtils.getStrengthRecords(userProgress);
    const todayVsLastWeek = WorkoutUtils.getTodayVsLastWeekComparison(userProgress, userProgress?.currentDay || 'Monday');
    const mostImproved = WorkoutUtils.getMostImprovedExercise(userProgress);

    switch (statType) {
      case 'program-progress':
        return {
          title: 'Program Progress Details',
          subtitle: '12-Week Workout Journey Analysis',
          content: 'Track your consistency and progress throughout the 12-week program with interactive charts.'
        };
      case 'weekly-improvements':
        return {
          title: 'Weekly Improvements Details',
          subtitle: 'Weight gains compared to last week',
          content: 'Detailed weekly improvement analysis will go here...'
        };
      case 'most-improved':
        return {
          title: 'Most Improved Exercise Details',
          subtitle: 'Biggest strength gains since start',
          content: 'Detailed most improved exercise information will go here...'
        };
      case 'personal-records':
        return {
          title: 'Personal Records Details',
          subtitle: 'Your strongest lifts and highest volume',
          content: 'Detailed personal records information will go here...'
        };
      case 'workout-summary':
        return {
          title: 'Workout Summary Details',
          subtitle: 'Your complete training overview',
          content: 'Detailed workout summary information will go here...'
        };
      default:
        return {
          title: 'Stat Not Found',
          subtitle: 'Unknown statistic type',
          content: 'The requested statistic could not be found.'
        };
    }
  };

  const statData = getStatData();

  if (!statType) {
    return (
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
        <HeaderBar title="Stat Not Found" onBack={handleClose} showBackButton={true} />
        <Container maxWidth="md" sx={{ mt: 'calc(56px + env(safe-area-inset-top, 0px) + 24px)', pb: 4, px: { xs: 2, sm: 3 } }}>
          <Alert severity="error" sx={{ mt: 4 }}>
            <Typography variant="h6" gutterBottom>Statistic Not Found</Typography>
            <Typography>The requested statistic could not be found.</Typography>
          </Alert>
        </Container>
      </Box>
    );
  }

  const renderContent = () => {
    switch (statType) {
      case 'program-progress':
        return (
          <Stack spacing={3}>
            {/* Subtitle */}
            <Typography 
              variant={isMobile ? "subtitle1" : "h6"} 
              color="text.secondary" 
              textAlign="center"
              sx={{ px: { xs: 1, sm: 0 } }}
            >
              {statData.subtitle}
            </Typography>

            {/* Overview Card */}
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
                  Program Overview
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {statData.content}
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  gap: { xs: 1.5, sm: 2 }, 
                  flexWrap: 'wrap',
                  justifyContent: { xs: 'space-between', sm: 'flex-start' }
                }}>
                  <Box sx={{ minWidth: { xs: '45%', sm: 'auto' } }}>
                    <Typography variant={isMobile ? "h5" : "h4"} color="primary.main" fontWeight="bold">
                      {stats.currentWeek}/12
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Current Week
                    </Typography>
                  </Box>
                  <Box sx={{ minWidth: { xs: '45%', sm: 'auto' } }}>
                    <Typography variant={isMobile ? "h5" : "h4"} color="success.main" fontWeight="bold">
                      {Math.round((stats.currentWeek / 12) * 100)}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Program Complete
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Weekly Progress Charts */}
            <WeeklyProgressCharts userProgress={userProgress} />

            {/* Additional Insights Card */}
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
                  Progress Insights
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  Use the dropdown above to explore different views of your weekly progress. 
                  The completion trend shows your consistency over time, while the heatmap 
                  provides a visual calendar of your workout schedule.
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        );

      case 'workout-summary':
        const overallStats = WorkoutUtils.getOverallStats(userProgress);
        const consistencyMetrics = WorkoutUtils.getTrainingConsistencyMetrics(userProgress);
        const varietyMetrics = WorkoutUtils.getExerciseVarietyMetrics(userProgress);
        
        return (
          <Stack spacing={3}>
            {/* Subtitle */}
            <Typography 
              variant={isMobile ? "subtitle1" : "h6"} 
              color="text.secondary" 
              textAlign="center"
              sx={{ px: { xs: 1, sm: 0 } }}
            >
              {statData.subtitle}
            </Typography>

            {/* Overview Card */}
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
                  Training Overview
                </Typography>
                <Typography variant="body2" sx={{ mb: 3, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  Complete breakdown of your workout performance with interactive charts and detailed metrics.
                </Typography>
                <Box sx={{ 
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
                  gap: { xs: 2, sm: 3 }
                }}>
                  <Box>
                    <Typography variant={isMobile ? "h5" : "h4"} color="primary.main" fontWeight="bold">
                      {overallStats.totalWorkouts}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total Workouts
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant={isMobile ? "h5" : "h4"} color="success.main" fontWeight="bold">
                      {overallStats.totalVolume.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total Volume (lbs)
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant={isMobile ? "h5" : "h4"} color="warning.main" fontWeight="bold">
                      {overallStats.totalSets}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total Sets
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant={isMobile ? "h5" : "h4"} color="info.main" fontWeight="bold">
                      {overallStats.totalReps.toLocaleString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total Reps
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Additional Metrics Card */}
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
                  Performance Metrics
                </Typography>
                <Box sx={{ 
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
                  gap: { xs: 2, sm: 3 },
                  mb: 3
                }}>
                  <Box>
                    <Typography variant={isMobile ? "h5" : "h4"} color="primary.main" fontWeight="bold">
                      {overallStats.averageDuration > 0 ? `${overallStats.averageDuration} min` : 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Average Duration
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant={isMobile ? "h5" : "h4"} color="success.main" fontWeight="bold">
                      {overallStats.averageRPE > 0 ? overallStats.averageRPE : 'N/A'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Average RPE
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant={isMobile ? "h5" : "h4"} color="warning.main" fontWeight="bold">
                      {consistencyMetrics.weeklyConsistency}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Weekly Consistency
                    </Typography>
                  </Box>
                </Box>
                
                {/* Consistency and Variety Metrics */}
                <Box sx={{ 
                  display: 'grid',
                  gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' },
                  gap: { xs: 2, sm: 3 }
                }}>
                  <Box>
                    <Typography variant={isMobile ? "h6" : "h5"} color="info.main" fontWeight="bold">
                      {consistencyMetrics.currentStreak}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Current Streak
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant={isMobile ? "h6" : "h5"} color="info.main" fontWeight="bold">
                      {consistencyMetrics.longestStreak}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Longest Streak
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant={isMobile ? "h6" : "h5"} color="secondary.main" fontWeight="bold">
                      {varietyMetrics.totalUniqueExercises}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Unique Exercises
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant={isMobile ? "h6" : "h5"} color="secondary.main" fontWeight="bold">
                      {varietyMetrics.exercisesThisWeek}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      This Week
                    </Typography>
                  </Box>
                </Box>
                
                {/* Most Frequent Exercise */}
                {varietyMetrics.mostFrequentExercise.count > 0 && (
                  <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Most Frequent Exercise
                    </Typography>
                    <Typography 
                      variant={isMobile ? "body1" : "h6"} 
                      fontWeight="bold"
                      sx={{ wordBreak: 'break-word' }}
                    >
                      {varietyMetrics.mostFrequentExercise.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Performed {varietyMetrics.mostFrequentExercise.count} times
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>

            {/* Workout Summary Charts */}
            <WorkoutSummaryCharts userProgress={userProgress} />

            {/* Additional Insights Card */}
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
                  Training Insights & Duration Tracking
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, mb: 2 }}>
                  Use the dropdown above to explore different aspects of your training data. 
                  Volume trends show your progression over time, exercise distribution reveals 
                  which movements contribute most to your training, workout frequency displays 
                  your consistency across different days, duration trends track how long your 
                  workouts take, RPE accuracy shows how well you estimate effort levels, and 
                  consistency metrics provide insights into your training habits and variety.
                </Typography>
                
                <Box sx={{ 
                  p: 2, 
                  bgcolor: 'background.default', 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider'
                }}>
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    How Workout Duration is Calculated
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    Workout duration is automatically tracked from when you press "Start Workout" 
                    on the home page until you complete all exercises and press "Complete Workout". 
                    The timer runs in the background and calculates the total time spent on your 
                    workout session. This provides accurate data for analyzing your training 
                    efficiency and helps you understand how long different workout types take.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Stack>
        );

      case 'most-improved':
        const mostImproved = WorkoutUtils.getMostImprovedExercise(userProgress);
        return (
          <Stack spacing={3}>
            {/* Subtitle */}
            <Typography 
              variant={isMobile ? "subtitle1" : "h6"} 
              color="text.secondary" 
              textAlign="center"
              sx={{ px: { xs: 1, sm: 0 } }}
            >
              {statData.subtitle}
            </Typography>

            {/* Overview Card */}
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
                  Most Improved Exercise
                </Typography>
                <Typography variant="body2" sx={{ mb: 3, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  Detailed analysis of your biggest strength gains with progression tracking and RPE analysis.
                </Typography>
                {mostImproved.hasData ? (
                  <Box sx={{ 
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                    gap: { xs: 2, sm: 3 }
                  }}>
                    <Box>
                      <Typography 
                        variant={isMobile ? "h6" : "h4"} 
                        color="primary.main" 
                        fontWeight="bold"
                        sx={{ 
                          fontSize: { xs: '1.25rem', sm: '2.125rem' },
                          wordBreak: 'break-word'
                        }}
                      >
                        {mostImproved.exerciseName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Exercise
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant={isMobile ? "h5" : "h4"} color="success.main" fontWeight="bold">
                        +{mostImproved.improvement}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Improvement
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant={isMobile ? "h5" : "h4"} color="warning.main" fontWeight="bold">
                        {mostImproved.startWeight}→{mostImproved.currentWeight}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Weight Progress (lbs)
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                    Complete more workouts to see your most improved exercise.
                  </Typography>
                )}
              </CardContent>
            </Card>

            {/* Most Improved Charts */}
            <MostImprovedCharts userProgress={userProgress} />

            {/* Additional Insights Card */}
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
                  Improvement Insights
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  Track your strength progression over time with detailed weight and volume analysis. 
                  The RPE accuracy chart shows how well you're estimating your effort levels, 
                  while strength gains reveal your week-to-week improvements.
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        );

      case 'personal-records':
        const strengthRecords = WorkoutUtils.getStrengthRecords(userProgress);
        return (
          <Stack spacing={3}>
            {/* Subtitle */}
            <Typography 
              variant={isMobile ? "subtitle1" : "h6"} 
              color="text.secondary" 
              textAlign="center"
              sx={{ px: { xs: 1, sm: 0 } }}
            >
              {statData.subtitle}
            </Typography>

            {/* Overview Card */}
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
                  Personal Records Overview
                </Typography>
                <Typography variant="body2" sx={{ mb: 3, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  Comprehensive analysis of your personal records with timeline tracking and RPE patterns.
                </Typography>
                <Box sx={{ 
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                  gap: { xs: 2, sm: 3 }
                }}>
                  <Box>
                    <Typography variant={isMobile ? "h5" : "h4"} color="primary.main" fontWeight="bold">
                      {strengthRecords.highestWeight.hasData ? `${strengthRecords.highestWeight.weight} lbs` : 'No Data'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Highest Weight
                    </Typography>
                    {strengthRecords.highestWeight.hasData && (
                      <Typography 
                        variant="caption" 
                        color="text.secondary" 
                        sx={{ 
                          display: 'block',
                          wordBreak: 'break-word'
                        }}
                      >
                        {strengthRecords.highestWeight.exerciseName}
                      </Typography>
                    )}
                  </Box>
                  <Box>
                    <Typography variant={isMobile ? "h5" : "h4"} color="success.main" fontWeight="bold">
                      {strengthRecords.highestVolume.hasData 
                        ? `${strengthRecords.highestVolume.volume.toLocaleString()} lbs`
                        : 'No Data'
                      }
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Highest Volume
                    </Typography>
                    {strengthRecords.highestVolume.hasData && (
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ 
                          display: 'block',
                          wordBreak: 'break-word'
                        }}
                      >
                        {strengthRecords.highestVolume.exerciseName}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>

            {/* Personal Records Charts */}
            <PersonalRecordsCharts userProgress={userProgress} />

            {/* Additional Insights Card */}
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
                  Records Insights
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  Explore your personal records timeline to see when you achieved your best performances. 
                  The frequency analysis shows which exercises you're progressing fastest in, 
                  while RPE analysis reveals the effort levels associated with your best lifts.
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        );

      default:
        return (
          <Stack spacing={3}>
            {/* Subtitle */}
            <Typography 
              variant={isMobile ? "subtitle1" : "h6"} 
              color="text.secondary" 
              textAlign="center"
              sx={{ px: { xs: 1, sm: 0 } }}
            >
              {statData.subtitle}
            </Typography>

            {/* Placeholder Content */}
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="body2" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  {statData.content}
                </Typography>
              </CardContent>
            </Card>

            {/* Additional placeholder cards for detailed stats */}
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
                  Detailed Analysis
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  This section will contain detailed analysis and charts for this statistic.
                </Typography>
              </CardContent>
            </Card>

            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
                  Historical Data
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                  This section will show historical trends and progress over time.
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        );
    }
  };

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <HeaderBar title={statData.title} onBack={handleClose} showBackButton={true} />
      <Container maxWidth="md" sx={{ mt: 'calc(56px + env(safe-area-inset-top, 0px) + 24px)', pb: 4, px: { xs: 2, sm: 3 } }}>
        {renderContent()}
      </Container>
    </Box>
  );
}; 